/**
 * Where the baked diffuse SH probe grid is (ENG-561). Two sources, tried in
 * order — the SAME two-tier rule `navmeshGridKey.js` already documents for the
 * navmesh artifacts, and for the same reason:
 *
 *  1. SERVER (production) — the URL the asset-manager published for this
 *     environment's artifact, listed only when the object exists, so an empty
 *     candidate list means "not baked" rather than "go and check".
 *  2. LOCAL KEYED (dev) — `public/assets/environments/lightprobes/<splatCacheKey>.json`,
 *     written by `npm run bake-lightprobes` for local testing without a
 *     deployed bake service.
 *
 * Tier 2 existed for the navmesh and NOT for the probe grid, which made the
 * probe untestable on any stack whose asset manager cannot serve it — and a
 * local AAM cannot: `GET /api/org/environments/{id}` (the only endpoint that
 * publishes a `files` map) 404s there, and `POST .../bake` answers
 * `{"error":"bake service not configured"}`. So every locally-uploaded
 * environment read as permanently unbaked with no way to bake it, which is
 * exactly the gap `bake_navmesh.mjs` calls "local-first dev convenience".
 *
 * The server URL replaces a path this module used to COMPUTE from the splat's
 * path, which silently produced wrong paths once a second storage layout existed
 * (ENG-999). The dev path stays keyed by content hash and is ours, not the
 * server's, so it is still built here — the same split navmeshGridKey.js draws.
 */
import { splatCacheKey } from './envCubemapCache.js'
import { environmentFileUrl } from './environmentFiles.js'

/** The local keyed path a local bake writes to. Ours, so we build it.
 *
 *  The QUERY IS STRIPPED before hashing: the key names the resource, and the
 *  runtime's splat URL can carry a `?v=<splatVersion>` the bake tool's never
 *  does (`bake_navmesh.mjs` normalises through `new URL(...).pathname`). Hashing
 *  the busted URL would key the two to different files and the bake would read
 *  as missing on exactly the reload that was meant to pick it up. */
export async function lightProbeGridLocalUrl(splatUrl) {
  const resource = (splatUrl || '').split('?')[0]
  return `/assets/environments/lightprobes/${await splatCacheKey(resource)}.json`
}

/** [serverUrl?, localKeyedUrl] — the server entry dropped when it lists none. */
export async function lightProbeGridCandidates(splatUrl) {
  const served = await environmentFileUrl(splatUrl, 'lightprobes')
  const local = await lightProbeGridLocalUrl(splatUrl)
  return served ? [served, local] : [local]
}

/**
 * The SERVED grid URL, or null when the server lists none. Kept because it is
 * the honest answer to "does the server have this?" — which is a different
 * question from "is there a grid to load", and the status chip asks the first.
 */
export async function lightProbeGridUrl(splatUrl) {
  return environmentFileUrl(splatUrl, 'lightprobes')
}
